import {utils} from '@wallaby-ui/core';
import {map, sortBy} from 'lodash';
import * as React from 'react';
import {useMemo} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {DynamicFilter, Filter, FILTER_TYPE_MULTI_SELECT, FILTER_TYPE_SELECT} from 'src/js/lib/filtering';
import {FilterNode as FilterNodeModel, FilterSchema} from 'src/js/model/filters_config';

import FilterNodeSelectRemoveAll from './FilterNodeSelectRemoveAll';
import FilterNodeSelectRemoveItem from './FilterNodeSelectRemoveItem';
import {FilterTypeSelect} from './FilterNodeTypeSelect';
import {FilterNodeTypeWrapper} from './FilterNodeTypeWrapper';
import {getSelectOptionIdProperty} from '../filtering_helpers';

export const I18N = defineMessages({
  PLACEHOLDER: {
    id: 'filter.dropDown.placeholder',
    defaultMessage: 'Select...',
    description: 'placeholder in drop down input',
  },
  NO_OPTIONS: {
    id: 'filter.dropDown.noOptions',
    defaultMessage: 'No matches found',
    description: 'Dropdown label when no options are available',
  },
});

const STATIC_EMPTY_ARRAY = [];

export interface FilterNodeTypeSelectOwnProps {
  inputRef: (inputElement: HTMLInputElement) => void;
  setFocus: () => void;
  isOpened?: boolean;
  node: FilterNodeModel;
  changeFilter: (values: any) => void;
  multi?: boolean;
  removeFilter: (filter: Filter) => void;
  schema: FilterSchema;
  selectedFilter?: Filter;
  toggleOpened: () => void;
  options: any;
  isMui?: boolean;
  valueProperty: string;
}

type Props = FilterNodeTypeSelectOwnProps;

export const FilterNodeTypeSelectContainer: React.FC<Props> = (props) => {
  const {
    inputRef,
    isOpened = false,
    multi = false,
    node,
    removeFilter,
    selectedFilter,
    setFocus,
    toggleOpened,
    options,
    changeFilter,
    schema,
    isMui,
    ...restProps
  } = props;

  const intl = useIntl();
  const dlSelectComponents = useMemo(() => {
    return {
      ClearIndicator: FilterNodeSelectRemoveAll,
      MultiValueRemove: FilterNodeSelectRemoveItem,
    };
  }, []);

  const getOptionsFromFilterConfig = () => {
    let additionalOptions;
    const idProperty = getSelectOptionIdProperty(isMui);
    if (!options) {
      additionalOptions = node.getChildrenIds().map((childId) => {
        const filterNode = schema.getFilter(childId);
        const label = filterNode.getLabel();
        const translatedLabel =
          label && utils.TranslateUtils.hasTranslation(label, intl)
            ? utils.TranslateUtils.translate(label, intl)
            : label || filterNode.getName();
        return {
          [idProperty]: filterNode.getName(),
          label: translatedLabel,
        };
      });
    }
    return additionalOptions ? sortBy(additionalOptions, ['label']) : STATIC_EMPTY_ARRAY;
  };

  const getFilter = (value) => {
    const filterType = multi ? FILTER_TYPE_MULTI_SELECT : FILTER_TYPE_SELECT;
    const SelectFilter = DynamicFilter.getFilterClass(filterType);
    return new SelectFilter({
      node,
      type: filterType,
      property: node.getName(),
      value,
    });
  };

  const getValue = () => {
    return selectedFilter ? selectedFilter.getValue() : null;
  };

  const changeSelectedFilter = (value) => {
    const filterObj = getFilter(value);
    if (!value || value.length === 0) {
      removeFilter(filterObj);
    } else {
      changeFilter(filterObj);
    }
  };

  const isHidden = () => {
    if (node.getHideIfEmptyOrSingleValue()) {
      if (options.length <= 1) {
        return true;
      }
    }
    return false;
  };

  const handleChange = (value: any) => {
    if (multi) {
      const filterValue = map(value, (item) => item.id);
      changeSelectedFilter(filterValue);
    } else if (value) {
      changeSelectedFilter(value.id);
    } else if (!node.isNotRemovable()) {
      changeSelectedFilter(null);
    }
  };

  const filter = getFilter(undefined);
  const value = getValue();
  const hidden = isHidden();

  return hidden ? null : (
    <FilterNodeTypeWrapper
      filter={filter}
      isOpened={isOpened}
      node={node}
      removeFilter={removeFilter}
      setFocus={setFocus}
      selectedFilter={selectedFilter}
      toggleOpened={toggleOpened}
      isMui={isMui}
    >
      <FilterTypeSelect
        inputRef={inputRef}
        name={node.getName()}
        id={node.getName()}
        label={node.getLabel()}
        selectedValue={value}
        isMulti={multi}
        isClearable={!node.isNotRemovable()}
        noOptionsText={I18N.NO_OPTIONS.id}
        placeholderText={I18N.PLACEHOLDER.id}
        tabIndex={isOpened ? 0 : -1}
        onChange={handleChange}
        options={options ?? getOptionsFromFilterConfig()}
        components={dlSelectComponents}
        isMui={isMui}
        {...restProps}
      />
    </FilterNodeTypeWrapper>
  );
};

FilterNodeTypeSelectContainer.displayName = 'FilterNodeTypeSelectContainer';
